import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react"
import { API_BASE } from "../config/appConfig"
import { useAuth } from "./AuthContext"

const ClinicContext = createContext(null)

function normalizeClinicId(value) {
  if (!value) return ""
  return String(value).trim()
}

function normalizeClinicRows(rows) {
  if (!Array.isArray(rows)) return []

  return rows
    .map((item) => ({
      id: String(item?.clinicId || item?.clinic?.id || item?.id || "").trim(),
      name: String(
        item?.clinic?.name || item?.clinicName || item?.name || ""
      ).trim()
    }))
    .filter((item) => item.id)
}

export function buildAuthHeaders(includeJson = false) {
  const token = localStorage.getItem("odontoflow_token") || ""
  const clinicId = localStorage.getItem("odontoflow_clinic_id") || ""

  const headers = {}

  if (includeJson) {
    headers["Content-Type"] = "application/json"
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  if (clinicId && clinicId !== "ALL") {
    headers["x-clinic-id"] = clinicId
  }

  return headers
}

async function fetchInitContext() {
  const res = await fetch(`${API_BASE}/onboarding/init-context`, {
    headers: buildAuthHeaders()
  })

  if (!res.ok) {
    throw new Error("No se pudo cargar el contexto inicial")
  }

  return res.json().catch(() => null)
}

async function fetchUserClinics() {
  const res = await fetch(`${API_BASE}/multi-clinic/user-clinics`, {
    headers: buildAuthHeaders()
  })

  if (!res.ok) {
    throw new Error("No se pudieron cargar las clínicas")
  }

  const data = await res.json().catch(() => [])
  return normalizeClinicRows(data)
}

async function bootstrapInitialClinics() {
  const res = await fetch(`${API_BASE}/multi-clinic/bootstrap-initial`, {
    method: "POST",
    headers: buildAuthHeaders(true),
    body: JSON.stringify({})
  })

  if (!res.ok) {
    throw new Error("No se pudo ejecutar el bootstrap inicial")
  }

  return res.json().catch(() => null)
}

export function ClinicProvider({ children }) {
  const { user } = useAuth()

  const [activeClinicId, setActiveClinicIdState] = useState("")
  const [clinics, setClinics] = useState([])
  const [loadingClinics, setLoadingClinics] = useState(true)
  const [resolvedUserId, setResolvedUserId] = useState("")
  const [initContext, setInitContext] = useState(null)

  const loadClinics = useCallback(async () => {
    setLoadingClinics(true)

    try {
      const userId = normalizeClinicId(user?.id || "")
      setResolvedUserId(userId)

      if (!userId) {
        setClinics([])
        setInitContext(null)
        setActiveClinicIdState("")
        return
      }

      const storedClinicId = normalizeClinicId(
        localStorage.getItem("odontoflow_clinic_id")
      )

      let resolvedClinics = []
      let resolvedDefaultClinicId = ""
      let resolvedInitContext = null

      try {
        resolvedInitContext = await fetchInitContext()
        setInitContext(resolvedInitContext)

        resolvedClinics = normalizeClinicRows(resolvedInitContext?.clinics || [])
        resolvedDefaultClinicId = normalizeClinicId(
          resolvedInitContext?.defaultClinicId
        )
      } catch (error) {
        console.warn("No se pudo cargar init-context:", error)
      }

      if (!resolvedClinics.length) {
        try {
          resolvedClinics = await fetchUserClinics()
        } catch (error) {
          console.warn("No se pudieron cargar clínicas desde backend:", error)
        }
      }

      if (!resolvedClinics.length) {
        try {
          await bootstrapInitialClinics()

          try {
            resolvedInitContext = await fetchInitContext()
            setInitContext(resolvedInitContext)

            resolvedClinics = normalizeClinicRows(
              resolvedInitContext?.clinics || []
            )
            resolvedDefaultClinicId = normalizeClinicId(
              resolvedInitContext?.defaultClinicId
            )
          } catch (error) {
            console.warn(
              "No se pudo recargar init-context después del bootstrap:",
              error
            )
          }

          if (!resolvedClinics.length) {
            resolvedClinics = await fetchUserClinics()
          }
        } catch (error) {
          console.warn("No se pudo ejecutar bootstrap inicial:", error)
        }
      }

      setClinics(resolvedClinics)

      setActiveClinicIdState((current) => {
        const normalizedCurrent = normalizeClinicId(current)

        if (
          normalizedCurrent &&
          normalizedCurrent === "ALL" &&
          resolvedClinics.length > 1
        ) {
          return "ALL"
        }

        if (
          normalizedCurrent &&
          resolvedClinics.some((item) => item.id === normalizedCurrent)
        ) {
          return normalizedCurrent
        }

        if (
          storedClinicId &&
          storedClinicId === "ALL" &&
          resolvedClinics.length > 1
        ) {
          return "ALL"
        }

        if (
          storedClinicId &&
          resolvedClinics.some((item) => item.id === storedClinicId)
        ) {
          return storedClinicId
        }

        if (
          resolvedDefaultClinicId &&
          resolvedClinics.some((item) => item.id === resolvedDefaultClinicId)
        ) {
          return resolvedDefaultClinicId
        }

        return resolvedClinics[0]?.id || ""
      })
    } finally {
      setLoadingClinics(false)
    }
  }, [user])

  useEffect(() => {
    loadClinics()
  }, [loadClinics])

  useEffect(() => {
    function handleClinicsUpdated() {
      loadClinics()
    }

    window.addEventListener("odontoflow:clinics-updated", handleClinicsUpdated)

    return () => {
      window.removeEventListener("odontoflow:clinics-updated", handleClinicsUpdated)
    }
  }, [loadClinics])

  useEffect(() => {
    if (activeClinicId) {
      localStorage.setItem("odontoflow_clinic_id", activeClinicId)
    } else {
      localStorage.removeItem("odontoflow_clinic_id")
    }
  }, [activeClinicId])

  const setActiveClinicId = useCallback((clinicId) => {
    setActiveClinicIdState(normalizeClinicId(clinicId))
  }, [])

  const setAvailableClinics = useCallback((clinicList) => {
    const normalized = normalizeClinicRows(clinicList)
    setClinics(normalized)

    setActiveClinicIdState((current) => {
      const normalizedCurrent = normalizeClinicId(current)

      if (normalizedCurrent === "ALL" && normalized.length > 1) {
        return "ALL"
      }

      if (
        normalizedCurrent &&
        normalized.some((item) => item.id === normalizedCurrent)
      ) {
        return normalizedCurrent
      }

      return normalized[0]?.id || ""
    })
  }, [])

  const clinicOptions = useMemo(() => {
    const map = new Map()

    for (const clinic of clinics) {
      if (!clinic?.id) continue

      map.set(clinic.id, {
        id: clinic.id,
        name: clinic.name || `Clínica ${clinic.id}`
      })
    }

    const rows = Array.from(map.values())

    if (rows.length > 1) {
      rows.unshift({
        id: "ALL",
        name: "Todas las clínicas"
      })
    }

    return rows
  }, [clinics])

  const activeClinic = useMemo(() => {
    return clinicOptions.find((item) => item.id === activeClinicId) || null
  }, [clinicOptions, activeClinicId])

  const value = useMemo(
    () => ({
      activeClinicId,
      activeClinic,
      clinicOptions,
      loadingClinics,
      loadClinics,
      setActiveClinicId,
      setAvailableClinics,
      resolvedUserId,
      initContext
    }),
    [
      activeClinicId,
      activeClinic,
      clinicOptions,
      loadingClinics,
      loadClinics,
      setActiveClinicId,
      setAvailableClinics,
      resolvedUserId,
      initContext
    ]
  )

  return (
    <ClinicContext.Provider value={value}>
      {children}
    </ClinicContext.Provider>
  )
}

export function useActiveClinic() {
  const context = useContext(ClinicContext)

  if (!context) {
    throw new Error("useActiveClinic must be used within a ClinicProvider")
  }

  return context
}